// client/src/components/pages/OperationOverview/RouteDetail.tsx
//
// The Route Detail tab of Operations Overview: the ST-00134 card redesign
// (neutral card surfaces, severity pills, a diverging variance meter per card,
// attention-first grouping), which replaced the original Route Detail cards in
// ST-00217. A Cards/Table toggle keeps the original's sortable table; the
// choice is remembered per browser.
// ST-00218 P3: the figures are the server's (on "Truck + time" routes adjusted dispatch also
// deducts received off-route and no receipt data); a route without (full) receipt data reads
// "n/a" and sits under Needs attention; the fallback note shows once under the strip.
// ST-00298: so does a route that received against nothing left dispatched (every dispatch
// carried forward) — never a variance against the float dust, and Avg variance leaves it out.
import { useState, type ReactNode } from 'react';
import { FaArrowRight, FaTable, FaTh } from 'react-icons/fa';
import { SegmentedControl } from '../../../types/commonComponents';
import MatchingNote from './MatchingNote';
import { adjustedDispatch, adjustedDispatchCount, naReasonOf } from './overviewKpis';
import { routeVariance, sortGroupings, type RouteSortConfig, type RouteSortKey } from './routeSort';
import type {
  FallbackNote,
  OverviewGrouping,
  OverviewKpis,
} from '../../../types/clientPages/operationOverview';
import { NO_VARIANCE_BASE_TEXT } from '../../../utils/variance';

/** 'na': no variance (no / partial receipt data, or nothing left dispatched to compare). */
type Band = 'ok' | 'watch' | 'warn' | 'crit' | 'na';

// Four severity bands (the retired original had five — green/emerald at
// <=1%/<=2% carried no decision value); the cards, the table and the legend
// share them. Hue pairings follow the theme's tint system: bg-<hue>-50 +
// text-<hue>-800 + border-<hue>-300 works in both light and dark (ST-00130).
const BANDS: Record<Band, { pill: string; fill: string; delta: string }> = {
  ok: {
    pill: 'bg-emerald-50 text-emerald-800 border-emerald-300',
    fill: 'bg-emerald-500',
    delta: 'text-slate-500',
  },
  watch: {
    pill: 'bg-amber-50 text-amber-800 border-amber-300',
    fill: 'bg-amber-500',
    delta: 'text-slate-500',
  },
  warn: {
    pill: 'bg-orange-50 text-orange-800 border-orange-300',
    fill: 'bg-orange-500',
    delta: 'text-orange-800 font-semibold',
  },
  crit: {
    pill: 'bg-red-50 text-red-800 border-red-300',
    fill: 'bg-red-500',
    delta: 'text-red-800 font-semibold',
  },
  na: {
    pill: 'bg-slate-100 text-slate-700 border-slate-300',
    fill: 'bg-slate-400',
    delta: 'text-slate-500',
  },
};

const getBand = (variance: number | null): Band => {
  if (variance === null) return 'na';
  const a = Math.abs(variance);
  if (a <= 2) return 'ok';
  if (a <= 3) return 'watch';
  if (a <= 5) return 'warn';
  return 'crit';
};

// "Origin to Destination" grouping names render as a two-line route; anything
// else falls back to the plain name.
const splitRoute = (name: string): [string, string] | null => {
  const idx = name.indexOf(' to ');
  if (idx === -1) return null;
  return [name.slice(0, idx), name.slice(idx + 4)];
};

// Diverging meter: center = 0, half-track = 10% variance, clamped.
const METER_HALF_PCT = 10;

/** One route card's figures (adjusted dispatch, variance band; variance null = n/a). */
interface RouteRow {
  grouping: OverviewGrouping;
  adjDispatch: number;
  dispatchCount: number;
  received: number;
  receivedCount: number;
  difference: number;
  variance: number | null;
  /** Why the variance is n/a, in words; null when there is one. */
  naReason: string | null;
  band: Band;
}

type ViewDetails = (grouping: OverviewGrouping) => void;

const toRouteRow = (grouping: OverviewGrouping): RouteRow => {
  const adjDispatch = adjustedDispatch(grouping);
  const received = grouping.totalReceiptWeight || 0;
  const variance = routeVariance(grouping);
  return {
    grouping,
    adjDispatch,
    dispatchCount: adjustedDispatchCount(grouping),
    received,
    receivedCount: grouping.totalReceiptCount || 0,
    difference: received - adjDispatch,
    variance,
    naReason: variance === null ? naReasonOf(grouping) : null,
    band: getBand(variance),
  };
};

const signed = (value: number, digits: number) =>
  `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
const signedOrNa = (value: number | null, digits: number) =>
  value === null ? 'n/a' : signed(value, digits);

// Cards or Table, remembered per browser. Storage can be missing or blocked
// (private window, site data cleared): then the page starts on Cards and the
// toggle still works for as long as the tab is open.
export type RouteDetailView = 'cards' | 'table';
const VIEW_STORAGE_KEY = 'lexibridge.operationsOverview.routeDetailView';

const readView = (): RouteDetailView => {
  try {
    return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'cards';
  } catch {
    return 'cards';
  }
};

const saveView = (view: RouteDetailView) => {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Not remembered; the choice still applies until the page is left.
  }
};

const VarianceMeter = ({ variance, band }: { variance: number | null; band: Band }) => {
  if (variance === null) {
    return <div className="relative h-1 rounded-full bg-slate-200 my-1" aria-hidden="true" />;
  }
  const width = (Math.min(Math.abs(variance), METER_HALF_PCT) / METER_HALF_PCT) * 50;
  const clamped = Math.abs(variance) > METER_HALF_PCT;
  return (
    <div className="relative h-1 rounded-full bg-slate-200 my-1" aria-hidden="true">
      <span className="absolute left-1/2 -top-0.5 -bottom-0.5 w-px bg-slate-400" />
      <span
        className={`absolute inset-y-0 rounded-full ${BANDS[band].fill}`}
        style={
          variance < 0
            ? { right: '50%', width: `${width}%`, minWidth: '2px' }
            : { left: '50%', width: `${width}%`, minWidth: '2px' }
        }
      />
      {clamped && (
        <span
          className={`absolute ${variance < 0 ? '-left-2.5' : '-right-2.5'} top-1/2 -translate-y-1/2 text-[8px] leading-none text-red-500`}
        >
          {variance < 0 ? '◂' : '▸'}
        </span>
      )}
    </div>
  );
};

const RouteCard = ({ route, onViewDetails }: { route: RouteRow; onViewDetails: ViewDetails }) => {
  const {
    grouping,
    adjDispatch,
    dispatchCount,
    received,
    receivedCount,
    difference,
    variance,
    naReason,
    band,
  } = route;
  const parts = splitRoute(grouping.name);
  const styles = BANDS[band];

  // n/a: the reason, not an "Over by" — receipts against nothing left dispatched are no variance.
  const deltaText =
    variance === null
      ? naReason
      : difference > 0
        ? `Over by ${Math.round(difference).toLocaleString()} MT`
        : difference < 0
          ? `Under by ${Math.abs(Math.round(difference)).toLocaleString()} MT`
          : 'Balanced';

  return (
    <div
      className="group bg-surface rounded-xl border border-slate-200 shadow-sm p-3.5 cursor-pointer hover:border-primary-300 hover:shadow-lg hover:-translate-y-px transition-all duration-200 motion-reduce:transition-none motion-reduce:transform-none"
      onClick={() => onViewDetails(grouping)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onViewDetails(grouping);
      }}
    >
      <div className="flex items-start justify-between gap-2.5 mb-3">
        {parts ? (
          <div className="flex gap-2 min-w-0">
            <div
              className="flex flex-col items-center pt-[5px] pb-1 flex-shrink-0"
              aria-hidden="true"
            >
              <span className="w-1.5 h-1.5 rounded-full border-[1.5px] border-slate-400" />
              <span className="flex-1 min-h-[8px] w-0 border-l border-dashed border-slate-300 my-0.5" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            </div>
            <div className="min-w-0 flex flex-col gap-0.5">
              <span
                className="text-[13px] font-semibold text-slate-900 leading-tight truncate"
                title={grouping.name}
              >
                {parts[0]}
              </span>
              <span className="text-[13px] font-semibold text-slate-900 leading-tight truncate">
                {parts[1]}
              </span>
            </div>
          </div>
        ) : (
          <h3 className="text-[13px] font-semibold text-slate-900 leading-tight break-words min-w-0">
            {grouping.name}
          </h3>
        )}
        <span
          className={`flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full border tabular-nums ${styles.pill}`}
          title={naReason ? `Variance not available: ${naReason}` : undefined}
        >
          {signedOrNa(variance, 1)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[11px] text-slate-500">Dispatched</p>
          <p className="text-base font-bold text-slate-900 tabular-nums leading-tight">
            {Math.round(adjDispatch).toLocaleString()}
            <span className="text-[10px] font-medium text-slate-400 ml-0.5">MT</span>
          </p>
          <p className="text-[11px] text-slate-400 tabular-nums">
            {dispatchCount.toLocaleString()} loads
          </p>
        </div>
        <div>
          <p className="text-[11px] text-slate-500">Received</p>
          <p className="text-base font-bold text-slate-900 tabular-nums leading-tight">
            {Math.round(received).toLocaleString()}
            <span className="text-[10px] font-medium text-slate-400 ml-0.5">MT</span>
          </p>
          <p className="text-[11px] text-slate-400 tabular-nums">
            {receivedCount.toLocaleString()} loads
          </p>
        </div>
      </div>

      <VarianceMeter variance={variance} band={band} />

      <div className="flex items-center justify-between mt-2">
        <span className={`text-xs tabular-nums ${styles.delta}`}>{deltaText}</span>
        <span className="flex items-center gap-1 text-xs font-semibold text-primary-600 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity motion-reduce:transition-none">
          Open <FaArrowRight className="w-2.5 h-2.5" />
        </span>
      </div>
    </div>
  );
};

const GroupSection = ({
  title,
  routes,
  onViewDetails,
}: {
  title: string;
  routes: RouteRow[];
  onViewDetails: ViewDetails;
}) => {
  if (routes.length === 0) return null;
  // A route without a variance (no receipt data) has no meaningful difference either.
  const netDiff = routes.reduce((sum, r) => (r.variance === null ? sum : sum + r.difference), 0);
  return (
    <section className="mb-7 last:mb-0">
      <div className="flex items-baseline justify-between px-0.5 mb-2.5">
        <h2 className="text-[13px] font-bold text-slate-900">{title}</h2>
        <span className="text-xs text-slate-500 tabular-nums">
          {routes.length} {routes.length === 1 ? 'route' : 'routes'} · net{' '}
          {netDiff >= 0 ? '+' : '-'}
          {Math.abs(Math.round(netDiff)).toLocaleString()} MT
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
        {routes.map((route) => (
          <RouteCard key={route.grouping.id} route={route} onViewDetails={onViewDetails} />
        ))}
      </div>
    </section>
  );
};

// The original Route Detail table (ST-00217 kept it): same columns, same sort,
// a click on a row opens Route Summary. Columns narrow away on small screens.
const SortHeader = ({
  column,
  sort,
  onSort,
  align,
  className = '',
  children,
}: {
  column: RouteSortKey;
  sort: RouteSortConfig;
  onSort: (key: RouteSortKey) => void;
  align: 'left' | 'right' | 'center';
  className?: string;
  children: ReactNode;
}) => {
  const active = sort.key === column;
  const justify =
    align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
      className={`p-0 ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`flex w-full items-center gap-1 sm:gap-2 ${justify} ${column === 'name' ? 'px-3' : 'px-2'} sm:px-4 2xl:px-6 py-3 sm:py-3.5 text-[10px] sm:text-xs font-semibold text-slate-600 uppercase tracking-wider hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500`}
      >
        {children}
        <span aria-hidden="true" className={active ? 'text-slate-700' : 'text-slate-400'}>
          {active ? (sort.direction === 'asc' ? '↑' : '↓') : '⇅'}
        </span>
      </button>
    </th>
  );
};

const RouteTable = ({
  routes,
  sort,
  onSort,
  onViewDetails,
}: {
  routes: RouteRow[];
  sort: RouteSortConfig;
  onSort: (key: RouteSortKey) => void;
  onViewDetails: ViewDetails;
}) => (
  <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead>
          <tr className="bg-slate-50">
            <SortHeader column="name" sort={sort} onSort={onSort} align="left">
              <span>
                <span className="hidden sm:inline">Route </span>Name
              </span>
            </SortHeader>
            <SortHeader
              column="dispatched"
              sort={sort}
              onSort={onSort}
              align="right"
              className="max-sm:hidden"
            >
              <span>
                <span className="hidden md:inline">Dispatched</span>
                <span className="md:hidden">Disp.</span> (MT)
              </span>
            </SortHeader>
            <SortHeader
              column="received"
              sort={sort}
              onSort={onSort}
              align="right"
              className="max-sm:hidden"
            >
              <span>
                <span className="hidden md:inline">Received</span>
                <span className="md:hidden">Recv.</span> (MT)
              </span>
            </SortHeader>
            <SortHeader
              column="loadsDispatched"
              sort={sort}
              onSort={onSort}
              align="right"
              className="max-lg:hidden"
            >
              Loads Out
            </SortHeader>
            <SortHeader
              column="loadsReceived"
              sort={sort}
              onSort={onSort}
              align="right"
              className="max-lg:hidden"
            >
              Loads In
            </SortHeader>
            <SortHeader
              column="difference"
              sort={sort}
              onSort={onSort}
              align="right"
              className="max-md:hidden"
            >
              Diff (MT)
            </SortHeader>
            <SortHeader column="variance" sort={sort} onSort={onSort} align="center">
              Variance
            </SortHeader>
            <th
              scope="col"
              className="px-2 sm:px-4 2xl:px-6 py-3 sm:py-3.5 text-center text-[10px] sm:text-xs font-semibold text-slate-600 uppercase tracking-wider max-sm:hidden"
            >
              Action
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {routes.map((route) => {
            const { grouping, band } = route;
            const styles = BANDS[band];
            return (
              <tr
                key={grouping.id}
                className="group cursor-pointer transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-primary-50"
                onClick={() => onViewDetails(grouping)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onViewDetails(grouping);
                }}
              >
                <td className="px-3 sm:px-4 2xl:px-6 py-3 sm:py-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span
                      className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${styles.fill}`}
                      aria-hidden="true"
                    />
                    <span className="text-xs sm:text-sm font-medium text-slate-900">
                      {grouping.name}
                    </span>
                  </div>
                </td>
                <td className="px-2 sm:px-4 2xl:px-6 py-3 sm:py-4 whitespace-nowrap text-right max-sm:hidden text-xs sm:text-sm font-medium text-slate-900 tabular-nums">
                  {Math.round(route.adjDispatch).toLocaleString()}
                </td>
                <td className="px-2 sm:px-4 2xl:px-6 py-3 sm:py-4 whitespace-nowrap text-right max-sm:hidden text-xs sm:text-sm font-medium text-slate-900 tabular-nums">
                  {Math.round(route.received).toLocaleString()}
                </td>
                <td className="px-2 sm:px-4 2xl:px-6 py-3 sm:py-4 whitespace-nowrap text-right max-lg:hidden text-sm text-slate-600 tabular-nums">
                  {route.dispatchCount.toLocaleString()}
                </td>
                <td className="px-2 sm:px-4 2xl:px-6 py-3 sm:py-4 whitespace-nowrap text-right max-lg:hidden text-sm text-slate-600 tabular-nums">
                  {route.receivedCount.toLocaleString()}
                </td>
                <td
                  className={`px-2 sm:px-4 2xl:px-6 py-3 sm:py-4 whitespace-nowrap text-right max-md:hidden text-xs sm:text-sm tabular-nums ${styles.delta}`}
                >
                  {route.difference > 0 ? '+' : ''}
                  {Math.round(route.difference).toLocaleString()}
                </td>
                <td className="px-2 sm:px-4 2xl:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                  <span
                    className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full border text-[10px] sm:text-xs font-bold tabular-nums ${styles.pill}`}
                    title={route.naReason ? `Variance not available: ${route.naReason}` : undefined}
                  >
                    {signedOrNa(route.variance, 2)}
                  </span>
                </td>
                <td className="px-2 sm:px-4 2xl:px-6 py-3 sm:py-4 whitespace-nowrap text-center max-sm:hidden">
                  <span
                    className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-medium text-primary-600 bg-primary-50 rounded-lg group-hover:bg-primary-100 transition-colors"
                    aria-hidden="true"
                  >
                    Details <FaArrowRight className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const LEGEND = [
  { label: 'On target ≤ 2%', dot: 'bg-emerald-500' },
  { label: 'Watch 2–3%', dot: 'bg-amber-500' },
  { label: 'Warning 3–5%', dot: 'bg-orange-500' },
  { label: 'Critical > 5%', dot: 'bg-red-500' },
];
const LEGEND_NA = { label: 'n/a: no receipt data', dot: 'bg-slate-400' };
const LEGEND_NO_BASE = { label: 'n/a: nothing left dispatched', dot: 'bg-slate-400' };

export interface RouteDetailProps {
  /** The route cards on screen (filterRouteCards output). */
  groupings: OverviewGrouping[];
  /** The KPI strip (computeOverviewKpis over the same cards). */
  summaryStats: OverviewKpis;
  /** The search box's text; with a search the strip says it covers the matching routes only. */
  searchTerm?: string;
  /** The table's sort, kept by the page so the PDF export follows it. */
  sortConfig: RouteSortConfig;
  onSort: (key: RouteSortKey) => void;
  onViewDetails: ViewDetails;
  /** ST-00218 P3: shown once when a "Truck + time" route fell back to delivery-number matching. */
  matchingNote?: FallbackNote | null;
}

const RouteDetail = ({
  groupings,
  summaryStats,
  searchTerm = '',
  sortConfig,
  onSort,
  onViewDetails,
  matchingNote = null,
}: RouteDetailProps) => {
  const [view, setView] = useState<RouteDetailView>(readView);
  const changeView = (next: RouteDetailView) => {
    setView(next);
    saveView(next);
  };

  const routes = groupings.map(toRouteRow);
  // Worst first; routes without a variance (no receipt data) after them, by name.
  const attention = routes
    .filter((r) => r.band === 'warn' || r.band === 'crit' || r.band === 'na')
    .sort((a, b) => {
      if (a.variance === null || b.variance === null) {
        if (a.variance !== b.variance) return a.variance === null ? 1 : -1;
        return a.grouping.name.localeCompare(b.grouping.name);
      }
      return Math.abs(b.variance) - Math.abs(a.variance);
    });
  const onTarget = routes
    .filter((r) => r.band === 'ok' || r.band === 'watch')
    .sort((a, b) => a.grouping.name.localeCompare(b.grouping.name));

  const kpis: Array<{ label: string; value: string; unit?: string }> = [
    { label: 'Routes', value: summaryStats.totalRoutes.toLocaleString() },
    {
      label: 'Dispatched',
      value: Math.round(summaryStats.totalDispatched).toLocaleString(),
      unit: 'MT',
    },
    {
      label: 'Received',
      value: Math.round(summaryStats.totalReceived).toLocaleString(),
      unit: 'MT',
    },
    { label: 'Total loads', value: summaryStats.totalLoads.toLocaleString() },
  ];
  const avgVariance = summaryStats.avgVariance;
  const avgOk = avgVariance !== null && Math.abs(avgVariance) <= 2;
  const excludedRoutes = summaryStats.varianceExcludedRoutes || 0;
  const avgTitle =
    avgVariance === null
      ? `Not available: ${NO_VARIANCE_BASE_TEXT}`
      : excludedRoutes > 0
        ? `Leaves out ${excludedRoutes} route${excludedRoutes === 1 ? '' : 's'}: ${NO_VARIANCE_BASE_TEXT}`
        : undefined;
  const legendNa = [
    ...(routes.some((r) => r.naReason !== null && r.naReason !== NO_VARIANCE_BASE_TEXT)
      ? [LEGEND_NA]
      : []),
    ...(routes.some((r) => r.naReason === NO_VARIANCE_BASE_TEXT) ? [LEGEND_NO_BASE] : []),
  ];
  const search = searchTerm.trim();

  return (
    <div>
      {/* KPI strip — one surface with hairline dividers instead of five floating cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-px bg-slate-200 rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-4 sm:mb-6">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bg-surface px-3.5 py-3">
            <p className="text-[11px] text-slate-500">{kpi.label}</p>
            <p className="text-lg sm:text-xl font-bold text-slate-900 tabular-nums truncate">
              {kpi.value}
              {kpi.unit && (
                <span className="text-[10px] sm:text-xs font-medium text-slate-400 ml-1">
                  {kpi.unit}
                </span>
              )}
            </p>
          </div>
        ))}
        <div className="bg-surface px-3.5 py-3 col-span-2 sm:col-span-1" title={avgTitle}>
          <p className="text-[11px] text-slate-500">Avg variance</p>
          <p
            className={`text-lg sm:text-xl font-bold tabular-nums ${avgVariance === null ? 'text-slate-500' : avgOk ? 'text-emerald-800' : 'text-red-800'}`}
          >
            {signedOrNa(avgVariance, 2)}
          </p>
          {excludedRoutes > 0 && (
            <p className="text-[10px] text-slate-500">
              excl. {excludedRoutes} n/a route{excludedRoutes === 1 ? '' : 's'}
            </p>
          )}
        </div>
      </div>
      {search !== '' && summaryStats.source === 'cards' && (
        <p className="-mt-2 sm:-mt-4 mb-3 sm:mb-4 text-xs text-slate-500">
          Totals for the {summaryStats.totalRoutes} route
          {summaryStats.totalRoutes === 1 ? '' : 's'} matching “{search}”.
        </p>
      )}
      <MatchingNote note={matchingNote} className="-mt-1 sm:-mt-3 mb-4" />

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 mb-4 px-0.5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <span className="text-xs text-slate-500">Variance bands:</span>
          {[...LEGEND, ...legendNa].map((item) => (
            <span
              key={item.label}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500"
            >
              <span className={`w-2 h-2 rounded-full ${item.dot}`} aria-hidden="true" />
              {item.label}
            </span>
          ))}
        </div>
        <SegmentedControl
          ariaLabel="Route Detail layout"
          value={view}
          onChange={changeView}
          options={[
            { value: 'cards', label: 'Cards', icon: FaTh },
            { value: 'table', label: 'Table', icon: FaTable },
          ]}
        />
      </div>

      {view === 'table' ? (
        <RouteTable
          routes={sortGroupings(groupings, sortConfig).map(toRouteRow)}
          sort={sortConfig}
          onSort={onSort}
          onViewDetails={onViewDetails}
        />
      ) : (
        <>
          <GroupSection title="Needs attention" routes={attention} onViewDetails={onViewDetails} />
          <GroupSection title="On target" routes={onTarget} onViewDetails={onViewDetails} />
        </>
      )}
    </div>
  );
};

export default RouteDetail;
